import * as d3 from "d3";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import { normalizeString as normalizeId } from "@/utils/stringUtils";
import { addTooltip } from "@/components/VivoPlots/functions/utils/tooltipFunctions";
import { hasNormalizedSelectedId } from "@/components/VivoPlots/functions/utils/selectedIdUtils";
import { palettes } from "./palletes";
import { tooltipPosition } from "../../utils/tickTooltip";

interface PointData {
  subjectId: string | null;
  /** Set only on a point built from one source row; see createPlotData. */
  entityKey?: string | null;
  subjectValue?: number | null;
  fill?: string | null;
  isOutlier?: boolean;
  xLabel?: string | null;
  x?: number | string;
  yLabel?: string | null;
  y?: number | string;
}

interface Point {
  x: number;
  y: number;
  data: PointData;
}

interface GroupDatum {
  points: Point[];
}

interface FillScale {
  scale?: (fill: string | null | undefined) => string;
  strokeScale?: (fill: string | null | undefined) => string;
  [key: string]: unknown;
}

interface PlotConfig {
  ssjIdValue?: string;
  selectedIdName?: string;
  palette?: "Blue" | "Magenta" | "Orange" | "Green" | "Gradient" | "Mixed";
  setClickedSubject?: (subject: string) => void;
  onPointActivate?: (entityKey: string, event: MouseEvent) => void;
}

const radiusConstant = {
  default: 4.5,
  clicked: 6,
  focal: 6,
  hovered: 7,
};

const strokeWidthConstant = {
  default: 1,
  clicked: 2,
  focal: 2,
  hovered: 2.5,
};

const opacityConstant = {
  default: 0.5,
  scatter: 0.9,
  muted: 0.2,
  clicked: 1,
  focal: 1,
};

export const drawCircles = (
  circleContainer: d3.Selection<d3.BaseType, GroupDatum, d3.BaseType, unknown>,
  normalizedId: string | undefined,
  plotConfig: PlotConfig,
  plotClassName: string,
  fillScale: FillScale = {},
  isScatter = false,
) => {
  addTooltip(plotClassName);

  circleContainer
    .selectAll("circle")
    .data((d) => d.points)
    .join("circle")
    .attr("cx", (d) => {
      return d.x;
    })
    .attr("cy", (d) => d.y)
    .attr("r", (d) => pointRadius(d, normalizedId, plotConfig))
    .attr("original-radius", (d) => pointRadius(d, undefined, plotConfig))
    .attr(
      "class",
      (d) =>
        `circle ${pointClass(d, normalizedId, plotConfig)} ${
          isScatter ? "scatter" : "swarm"
        }`,
    )
    .attr("fill", (d) => {
      return (pointColor(d, normalizedId, plotConfig, fillScale) ?? null) as
        | string
        | null;
    })
    .attr(
      "original-fill",
      (d) =>
        (pointColor(d, undefined, plotConfig, fillScale) ?? null) as
          | string
          | null,
    )
    .attr(
      "stroke",
      (d) =>
        (pointStroke(d, normalizedId, plotConfig, fillScale) ?? null) as
          | string
          | null,
    )
    .attr(
      "original-stroke",
      (d) =>
        (pointStroke(d, undefined, plotConfig, fillScale) ?? null) as
          | string
          | null,
    )

    .attr("id", (d) => {
      return `id${normalizeId(d.data.subjectId ?? "")}`;
    })
    .style("opacity", (d) =>
      pointOpacity(d, normalizedId, plotConfig, false, isScatter),
    )
    .attr("original-opacity", (d) =>
      pointOpacity(d, undefined, plotConfig, false, isScatter),
    );

  circleContainer
    .selectAll("circle")
    .on("mouseover", function (event, _d) {
      const d = _d as unknown as Point;
      const tooltip = d3.select(`.plot-container.${plotClassName} .tooltip`);
      d3.select(this)
        .style("opacity", opacityConstant.clicked)
        .attr("r", radiusConstant.hovered)
        .attr("stroke-width", strokeWidthConstant.hovered)
        .raise();
      tooltip.transition().style("opacity", 0.9);
      const container = d3.select(`.plot-container.${plotClassName}`);

      const _pos1 = tooltipPosition(
        event,
        container as unknown as d3.Selection<HTMLElement, unknown, any, any>,
        tooltip as unknown as d3.Selection<HTMLDivElement, unknown, any, any>,
      );
      const left = _pos1?.left ?? 0;
      const top = _pos1?.top ?? 0;

      const idLabel = plotConfig.selectedIdName
        ? toTitleCase(plotConfig.selectedIdName)
        : "Subject";

      tooltip
        .html(
          `${
            d.data.subjectId == null
              ? ""
              : `<p>${idLabel}: ${d.data.subjectId}</p>`
          }
             ${
               d.data.subjectValue
                 ? `<p>Value: ${d.data.subjectValue}</p>`
                 : `<p>X: ${d.data.xLabel ?? d.data.x}</p><p>Y: ${d.data.yLabel ?? d.data.y}</p>`
             }
              ${
                d.data.fill != null && d.data.fill !== "none"
                  ? `<p>Color: ${d.data.fill}</p>`
                  : ""
              }
            `,
        )

        .style("left", left + "px")
        .style("top", top + "px");
    })
    .on("mousemove", function (event) {
      const tooltip = d3.select(`.plot-container.${plotClassName} .tooltip`);
      const container = d3.select(`.plot-container.${plotClassName}`);
      const _pos2 = tooltipPosition(
        event,
        container as unknown as d3.Selection<HTMLElement, unknown, any, any>,
        tooltip as unknown as d3.Selection<HTMLDivElement, unknown, any, any>,
      );
      const left2 = _pos2?.left ?? 0;
      const top2 = _pos2?.top ?? 0;
      tooltip.style("left", left2 + "px").style("top", top2 + "px");
    })
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    .on("mouseout", function (event, d) {
      const tooltip = d3.select(`.plot-container.${plotClassName} .tooltip`);
      d3.select(this)
        .style("opacity", (d, i, nodes) => {
          return pointOpacityByClass(nodes[i]);
        })

        .attr("stroke-width", (d, i, nodes) => {
          return pointStrokeWidthByClass(nodes[i]);
        })

        .attr("r", (d, i, nodes) => pointRadiusByClass(nodes[i]));

      circleContainer
        .selectAll(".clicked")
        .style("opacity", (d, i, nodes) => pointOpacityByClass(nodes[i]))
        .attr("r", (d, i, nodes) => pointRadiusByClass(nodes[i]))
        .raise();

      tooltip.transition().style("opacity", 0);
    })
    .on("click", function (event, _d) {
      const d = _d as unknown as Point;
      if (d.data.subjectId != null) {
        plotConfig.setClickedSubject?.(normalizeId("" + d.data.subjectId));
      }
      // entityKey is set only on points built from a single source row, and is
      // the identifier unnormalized -- normalizeId is lossy, so subjectId, which
      // the highlight machinery matches on, cannot address a profile.
      if (d.data.entityKey == null) return;
      plotConfig.onPointActivate?.("" + d.data.entityKey, event);
    });
};

const pointRadius = (
  d: Point,
  normalizedId: string | undefined,
  plotConfig: PlotConfig,
  isClicked = false,
) => {
  if (isClicked) {
    return 6;
  }
  if (
    hasNormalizedSelectedId(normalizedId) &&
    normalizeId(d.data.subjectId ?? "") === normalizedId
  ) {
    return 6;
  }
  if (plotConfig.ssjIdValue) {
    if (
      normalizeId(d.data.subjectId ?? "") === normalizeId(plotConfig.ssjIdValue)
    ) {
      return 6;
    }
  }
  return 4.5;
};

const pointOpacity = (
  d: Point,
  normalizedId: string | undefined,
  plotConfig: PlotConfig,
  isClicked = false,
  isScatter = false,
) => {
  if (isClicked) {
    return opacityConstant.clicked;
  }
  if (hasNormalizedSelectedId(normalizedId)) {
    if (normalizeId(d.data.subjectId ?? "") === normalizedId) {
      return 1;
    }
    return opacityConstant.muted;
  }
  if (isScatter) {
    return opacityConstant.scatter;
  }

  return opacityConstant.default;
};

const pointColor = (
  d: Point,
  normalizedId: string | undefined,
  plotConfig: PlotConfig,
  fillScale: FillScale,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  isClicked = false,
) => {
  if (plotConfig.ssjIdValue) {
    if (
      hasNormalizedSelectedId(normalizedId) &&
      normalizeId(d.data.subjectId ?? "") === normalizedId
    ) {
      return fillScale
        ? fillScale.scale?.(d.data.fill)
        : palettes[plotConfig?.palette || "Blue"]?.fill[1];
    }
    if (d.data.isOutlier) {
      return "#991962";
    }
    return "#ccc";
  }
  if (d.data.isOutlier) {
    return "#991962";
  }

  return Object.keys(fillScale).length > 0
    ? fillScale?.scale?.(d.data.fill)
    : palettes[plotConfig?.palette || "Blue"]?.fill[1];
};

const pointStroke = (
  d: Point,
  normalizedId: string | undefined,
  plotConfig: PlotConfig,
  fillScale: FillScale,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  isClicked = false,
) => {
  if (d.data.isOutlier) {
    return "#991962";
  }
  if (plotConfig.ssjIdValue) {
    if (
      hasNormalizedSelectedId(normalizedId) &&
      normalizeId(d.data.subjectId ?? "") === normalizedId
    ) {
      return fillScale
        ? fillScale.strokeScale?.(d.data.fill)
        : palettes[plotConfig?.palette || "Blue"]?.stroke[1];
    }
    return "#556C75";
  }
  return Object.keys(fillScale).length > 0
    ? fillScale?.strokeScale?.(d.data.fill)
    : palettes[plotConfig?.palette || "Blue"]?.stroke[1];
};

const pointClass = (
  d: Point,
  normalizedId: string | undefined,
  plotConfig: PlotConfig,
) => {
  if (
    hasNormalizedSelectedId(normalizedId) &&
    normalizeId(d.data.subjectId ?? "") === normalizedId
  ) {
    return "clicked";
  }
  if (plotConfig.ssjIdValue) {
    if (
      normalizeId(d.data.subjectId ?? "") === normalizeId(plotConfig.ssjIdValue)
    ) {
      return "focal";
    }
  }
  return "";
};

export const pointOpacityByClass = (nodeInfo: d3.BaseType) => {
  const node = d3.select(nodeInfo);

  if (node.classed("clicked")) {
    return opacityConstant.clicked;
  }
  if (node.classed("focal")) {
    return opacityConstant.focal;
  }

  if (node.classed("muted")) {
    return opacityConstant.muted;
  }
  if (node.classed("scatter")) {
    return opacityConstant.scatter;
  }
  return node.attr("original-opacity");
};

export const pointStrokeWidthByClass = (nodeInfo: d3.BaseType) => {
  const node = d3.select(nodeInfo);
  if (node.classed("clicked")) {
    return strokeWidthConstant.clicked;
  }
  if (node.classed("focal")) {
    return strokeWidthConstant.focal;
  }
  if (node.classed("hovered")) {
    return strokeWidthConstant.hovered;
  }
  if (node.classed("muted")) {
    return strokeWidthConstant.default;
  }
  return strokeWidthConstant.default;
};

export const pointRadiusByClass = (nodeInfo: d3.BaseType) => {
  const node = d3.select(nodeInfo);
  if (node.classed("clicked")) {
    return radiusConstant.clicked;
  }
  if (node.classed("focal")) {
    return radiusConstant.focal;
  }
  if (node.classed("hovered")) {
    return radiusConstant.hovered;
  }
  if (node.classed("muted")) {
    return radiusConstant.default;
  }

  return radiusConstant.default;
};
